/**
 * AgentPanel.jsx — the Agent surface. ONE page, two actions, and the rules.
 *
 *   [Run baseline checks]   a button, not a destination. Suite picker beside it;
 *                           "Drive the device first" for states passive capture
 *                           can't reach. Free unless you opt into authoring.
 *   Ask …                   one box. Presence questions, rule verdicts and
 *                           COMPARISONS all resolve here — the server plans
 *                           "different internal_tag on adh vs banner in big
 *                           bets" into {key, where, by} and computes the answer.
 *   ▸ Rules (145)           collapsed. Disable a rule, or add your own.
 *
 * Two consolidations landed here, both of them undoing my own sprawl:
 *
 * 1. This replaced a FOUR-TAB version (Compile / Ask / Compare / Rules).
 *    Compile was a button wearing a tab. Compare was a FORM for what the Ask
 *    box already does from a sentence — both halves of one feature shipped side
 *    by side. Rules was a read-only table that told you to go edit elsewhere,
 *    which is a signpost pretending to be a feature.
 * 2. This ABSORBED the old Author mode, which duplicated all of it (its
 *    ask/RUN AUTO box, "Generate checks with AI", the suite runner and the
 *    quarantine panel). Author's two unique pieces — the suite picker and the
 *    Driven toggle — live here now.
 *
 * The cost contract stays visible everywhere: a green badge on a free answer, an
 * explicit confirm before anything bills. Do not add a path that spends without
 * the user asking.
 */
import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import Paper from '@mui/material/Paper';
import Typography from '@mui/material/Typography';
import Stack from '@mui/material/Stack';
import Chip from '@mui/material/Chip';
import Button from '@mui/material/Button';
import TextField from '@mui/material/TextField';
import Alert from '@mui/material/Alert';
import CircularProgress from '@mui/material/CircularProgress';
import Table from '@mui/material/Table';
import TableHead from '@mui/material/TableHead';
import TableBody from '@mui/material/TableBody';
import TableRow from '@mui/material/TableRow';
import TableCell from '@mui/material/TableCell';
import TableContainer from '@mui/material/TableContainer';
import Tooltip from '@mui/material/Tooltip';
import Accordion from '@mui/material/Accordion';
import AccordionSummary from '@mui/material/AccordionSummary';
import AccordionDetails from '@mui/material/AccordionDetails';
import ExpandMoreIcon from '@mui/icons-material/ExpandMore';
import Divider from '@mui/material/Divider';
import FormControl from '@mui/material/FormControl';
import InputLabel from '@mui/material/InputLabel';
import Select from '@mui/material/Select';
import MenuItem from '@mui/material/MenuItem';
import FormControlLabel from '@mui/material/FormControlLabel';
import Checkbox from '@mui/material/Checkbox';
import Box from '@mui/material/Box';
import { nameRules, ruleExpression } from '../utils/ruleExpression';
import { buildParamConformanceRows } from '../utils/buildParamConformance';
import { GAM_PARAM_DICTIONARY } from '../utils/gamParams';
import ParamConformanceTable from './ParamConformanceTable';
import { buildApsParamConformanceRows } from '../utils/buildApsParamConformance';
import ApsParamConformanceTable from './ApsParamConformanceTable';
import AskThread from './ask/AskThread';
import { threadKey, saveThread } from '../utils/askThread';
import { scopeSummary } from '../utils/askScope';
import { createAskRunner, loadInitialThread, resolveRowRef } from '../utils/askFlow';

import {
  agentPrepare, agentAsk, getSuiteChecks, listSuites, listQuarantine,
  runChecksDriven, listModels, getApsParamDictionary, getPrebidParamDictionary,
  getAskJob,
} from '../api/checks';
import { useTraffic } from '../context/TrafficContext';

const STATUS_COLOR = { pass: 'success', fail: 'error', warn: 'warning', info: 'info' };
const statusColor = (s) => STATUS_COLOR[s] || 'default';
const COUNT_ORDER = ['fail', 'warn', 'info', 'pass'];

const BASELINE = 'adtech-baseline';
/** Where authored rules go — the baseline itself is a committed, read-only builtin. */

const errText = (e) => (e && e.body && e.body.error) || (e && e.message) || 'something went wrong';

/* ─── Compile ─────────────────────────────────────────────────────────────── */

function CompileStrip({ rows, app, suite, setSuite, suites, prepared, setPrepared, driver }) {
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState(null);
  const [confirmSpend, setConfirmSpend] = useState(false);
  const [driven, setDriven] = useState(false);

  const canDrive = !!(driver && (driver.drivable || driver.webDrivable));

  const run = useCallback(async (compile) => {
    setBusy(true); setError(null);
    try {
      // Driven first when asked: passive capture can only check states the app
      // happened to reach. This manufactures them, THEN the same rules run.
      if (driven && canDrive && !compile) {
        await runChecksDriven({ suite, app, platform: driver?.platform || 'ios', device: driver?.device });
      }
      setPrepared(await agentPrepare({ rows, app, suite, compile }));
      setConfirmSpend(false);
    } catch (e) { setError(errText(e)); } finally { setBusy(false); }
  }, [rows, app, suite, driven, canDrive, driver, setPrepared]);

  return (
    <Stack spacing={1.5}>
      <Stack direction="row" spacing={1.5} alignItems="center" flexWrap="wrap" useFlexGap>
        <Button variant="contained" disabled={busy} onClick={() => run(false)}>
          {busy ? <CircularProgress size={18} /> : 'Run baseline checks'}
        </Button>

        <FormControl size="small" sx={{ minWidth: 190 }}>
          <InputLabel>Rules</InputLabel>
          <Select label="Rules" value={suite} onChange={(e) => { setSuite(e.target.value); setPrepared(null); }}>
            {suites.map((s) => (
              <MenuItem key={s.name} value={s.name}>
                {s.name}{typeof s.count === 'number' ? ` (${s.count})` : ''}
              </MenuItem>
            ))}
          </Select>
        </FormControl>

        <Tooltip title={canDrive
          ? 'Drives the tethered device through login/content flows first, so rules that need a logged-in or mid-article state actually have traffic to check.'
          : 'No tethered device available — runs pass over captured traffic only.'}>
          <FormControlLabel
            control={<Checkbox size="small" checked={driven && canDrive} disabled={!canDrive} onChange={(e) => setDriven(e.target.checked)} />}
            label={<Typography variant="caption">Drive the device first</Typography>}
          />
        </Tooltip>

        {prepared && prepared.ok && (
          <>
            <Typography sx={{ fontWeight: 600 }}>{prepared.applicable} of {prepared.total} ready</Typography>
            <Stack direction="row" spacing={0.75}>
              {COUNT_ORDER.filter((k) => prepared.counts[k]).map((k) => (
                <Chip key={k} size="small" color={statusColor(k)} label={`${prepared.counts[k]} ${k}`} />
              ))}
            </Stack>
            {!!prepared.skipped && (
              <Tooltip title="Rules targeting traffic this session does not contain. Not failures — just not applicable.">
                <Chip size="small" variant="outlined" label={`${prepared.skipped} n/a`} />
              </Tooltip>
            )}
          </>
        )}
      </Stack>

      {!prepared && !error && (
        <Typography variant="caption" sx={{ color: 'var(--muted)' }}>
          Runs every rule that applies to this session. Deterministic and free — no model is called.
        </Typography>
      )}

      {error && <Alert severity="error">{error}</Alert>}

      {prepared && prepared.ok && prepared.willSpend > 0 && (
        <Alert
          severity="info"
          action={confirmSpend
            ? <Button color="inherit" size="small" disabled={busy} onClick={() => run(true)}>Yes, author them</Button>
            : <Button color="inherit" size="small" onClick={() => setConfirmSpend(true)}>Author rules</Button>}
        >
          {prepared.gaps.map((g) => `${g.requestType} (${g.rowCount})`).join(', ')}
          {prepared.gaps.length === 1 ? ' is' : ' are'} in this session but no rule covers
          {prepared.gaps.length === 1 ? ' it' : ' them'}.
          {confirmSpend
            ? ` This calls the model ${prepared.willSpend} time${prepared.willSpend > 1 ? 's' : ''} and bills your account.`
            : ' Authoring rules for the gap costs tokens.'}
        </Alert>
      )}

      {prepared && prepared.compiled && (
        <Alert severity={prepared.compiled.ok ? 'success' : 'warning'}>
          {prepared.compiled.ok
            ? `Authored ${prepared.compiled.count} rule(s) for the gap.`
            : `Could not author the gap: ${prepared.compiled.error || 'unknown error'}`}
        </Alert>
      )}

      {prepared && !prepared.ok && (
        <Alert severity="warning">{(prepared.notes || []).join(' · ') || 'Nothing to run.'}</Alert>
      )}
    </Stack>
  );
}

/* ─── Ask ─────────────────────────────────────────────────────────────────── */

/** Which rung answered — nobody should have to guess what a question cost. */
const VIA_LABEL = {
  compare: { label: 'compared the captured requests — no model called', color: 'success' },
  params: { label: 'read straight from the captured params — no model called', color: 'success' },
  prepared: { label: 'answered from the compiled rules — no model called', color: 'success' },
  'compare+llm-plan': { label: 'model planned the query; the values are computed', color: 'info' },
  auto: { label: 'compiled and run, model narrated the result', color: 'info' },
  analyst: { label: 'full investigation — this one cost tokens', color: 'warning' },
};

/* ─── Model picker ───────────────────────────────────────────────────────────
 * The list comes from GET /proxy/models rather than living here, so the picker
 * can never offer a model the server would reject. AUTO_ONLY is the fallback
 * for an older proxy that has no /proxy/models — one option, which is exactly
 * the behaviour that proxy has.
 *
 * The choice is remembered, because the reason to pick a model (this session's
 * questions are hard, or I am watching spend) outlives a single question. It is
 * remembered as an ID the server may no longer allow, which is why the server
 * degrades an unknown model to Auto instead of erroring.
 */
const AUTO_ONLY = [{ id: 'auto', label: 'Auto', detail: 'Haiku first, escalating when the question needs it', tier: 'auto' }];
const MODEL_KEY = 'agent.ask.model';

function readStoredModel() {
  try { return localStorage.getItem(MODEL_KEY) || 'auto'; } catch { return 'auto'; }
}

// The label carries the OUTPUT rate only, to stay readable in a 132px control.
// It is a faithful proxy for relative cost here because all three models price
// output at exactly 5x input, so the ordering and the ratios between them are
// identical whichever half you show. The full rate is on the tooltip.
function modelOptionLabel(m) {
  if (!m || m.id === 'auto') return 'Auto';
  const price = typeof m.outputPer1M === 'number' ? ` · $${m.outputPer1M}/M out` : '';
  return `${m.label}${price}`;
}

// The second line of an option: what the model is for, plus the full list rate.
// The numbers come from the server (modelPolicy.js), not from here, so there is
// exactly one place to correct when pricing moves.
function modelDetail(m) {
  if (!m) return '';
  const rate = (typeof m.inputPer1M === 'number' && typeof m.outputPer1M === 'number')
    ? ` · $${m.inputPer1M}/M in`
    : '';
  return `${m.detail || ''}${rate}`;
}

/* ─── Ask thread ──────────────────────────────────────────────────────────
 * The conversation is utils/askThread's `{ threadId, turns }`, persisted per dashboard
 * session in localStorage (threadKey) so a round trip through the dashboard (an evidence
 * jump unmounts ChecksPage) or a reload keeps it. A turn that was still working when the
 * page went away is stored with its jobId and re-attached by the runner (utils/askFlow),
 * which keeps its trail. Nothing here reacts to a history_snapshot, empty or not, and
 * nothing clears the thread (AC 88). All decisions live in utils/askFlow.js; this is wiring.
 */

/* The pre-investigation rungs (compare / params / prepared) return a plan line, a per-host
 * table and a "differs" warning that the structured card has no slot for. They ride on the
 * turn's in-memory `extra` (not persisted) and render under the answer, so a host carrying
 * two values is still a visible finding rather than buried in prose. */
function AskExtras({ turn }) {
  const out = turn && turn.extra;
  if (!out) return null;
  const via = VIA_LABEL[out.via];
  const plan = out.plan;
  return (
    <>
      {out.modelNote && <Alert severity="warning" sx={{ py: 0 }}>{out.modelNote}</Alert>}
      {plan && (
        <Typography variant="caption" sx={{ display: 'block', color: 'var(--muted)', fontFamily: 'ui-monospace, Menlo, monospace' }}>
          {`key=${plan.key}`}
          {plan.where ? ` · where="${plan.where}"` : ''}
          {` · by=${plan.by}`}
          {typeof out.rowCount === 'number' ? ` · ${out.rowCount}/${out.scopedFrom} rows` : ''}
        </Typography>
      )}
      {Array.isArray(out.byHost) && out.byHost.length > 0 && (
        <TableContainer component={Paper} variant="outlined">
          <Table size="small">
            <TableHead><TableRow>
              <TableCell>Host</TableCell>
              <TableCell align="right">Sent</TableCell>
              <TableCell>{out.param}</TableCell>
            </TableRow></TableHead>
            <TableBody>
              {out.byHost.map((h) => {
                const conflicted = (out.inconsistentHosts || []).some((f) => f.host === h.name);
                const values = Array.isArray(h.values) ? h.values : [];
                return (
                  <TableRow key={h.name} hover>
                    <TableCell sx={{ fontFamily: 'ui-monospace, Menlo, monospace', fontSize: 11, wordBreak: 'break-all' }}>
                      {h.name}
                      {conflicted && <Chip size="small" color="warning" label="differs" sx={{ ml: 0.75 }} />}
                    </TableCell>
                    <TableCell align="right" sx={{ fontSize: 12 }}>{h.present}/{h.rowCount}</TableCell>
                    <TableCell sx={{ fontSize: 12, wordBreak: 'break-word' }}>
                      {values.length
                        ? values.map((v) => (
                          <Chip
                            key={v.value} size="small" sx={{ mr: 0.5, mb: 0.5 }}
                            color={conflicted ? 'warning' : 'default'}
                            label={v.value === '' ? `(empty) ×${v.count}` : `${v.value} ×${v.count}`}
                          />
                        ))
                        : <Typography variant="caption" sx={{ color: 'var(--muted)' }}>not sent</Typography>}
                    </TableCell>
                  </TableRow>
                );
              })}
            </TableBody>
          </Table>
        </TableContainer>
      )}
      {(out.inconsistentHosts || []).length > 0 && (
        <Alert severity="warning">
          {out.inconsistentHosts.map((f) => `${f.host} sends ${(f.values || []).length} different values`).join(' · ')}
          {' '}— one host serving one integration should publish one value.
        </Alert>
      )}
      {via && <Chip size="small" color={via.color} label={via.label} sx={{ alignSelf: 'flex-start' }} />}
    </>
  );
}

function AskBox({
  rows, app, prepared, suiteChecks, initialQuestion, redirectNotice, onDismissRedirectNotice, onJumpToRequest,
}) {
  const [models, setModels] = useState(AUTO_ONLY);
  const [model, setModel] = useState(readStoredModel);
  // Answers pushed over the dashboard's own WS: the fast path. The runner's poll is what
  // makes the feature work when this never arrives (and is what feeds the live trail).
  // `|| {}` because the context defaults to null outside its provider: rendered there,
  // the panel loses the fast path and the scope bar's session rows, it does not crash.
  const { askJobs, sessionId, liveTrafficGroups } = useTraffic() || {};
  const key = threadKey(sessionId);

  const [thread, setThread] = useState(() => loadInitialThread({
    local: window.localStorage, session: window.sessionStorage, sessionId,
  }));
  const threadRef = useRef(thread);
  const store = useMemo(() => ({
    get: () => threadRef.current,
    set: (t) => { threadRef.current = t; setThread(t); },
  }), []);
  const runnerRef = useRef(null);

  useEffect(() => {
    let live = true;
    listModels()
      .then((r) => { if (live && Array.isArray(r && r.models) && r.models.length) setModels(r.models); })
      .catch(() => { /* older proxy — Auto is the only honest option, already set */ });
    return () => { live = false; };
  }, []);

  // One runner per mount. Re-attaching to a working turn after a reload or a remount is
  // resume(); dispose() stops its polling when the page unmounts (the job keeps running
  // server-side and the next mount picks it up from the stored jobId).
  useEffect(() => {
    const runner = createAskRunner({
      store, agentAsk, getAskJob, sleep: (ms) => new Promise((r) => setTimeout(r, ms)),
    });
    runnerRef.current = runner;
    runner.resume();
    return () => { runner.dispose(); if (runnerRef.current === runner) runnerRef.current = null; };
  }, [store]);

  // Save on every change; reload when the dashboard session id changes (a different
  // session is a different conversation). The first render for a new key only reloads,
  // so the old thread is never written under the new key.
  const loadedKey = useRef(key);
  useEffect(() => {
    if (loadedKey.current !== key) {
      loadedKey.current = key;
      store.set(loadInitialThread({ local: window.localStorage, session: window.sessionStorage, sessionId }));
      return;
    }
    saveThread(window.localStorage, key, thread);
  }, [key, thread, sessionId, store]);

  // WS push: settle any working turn whose job finished. Idempotent in the runner.
  useEffect(() => {
    const runner = runnerRef.current;
    if (!runner || !askJobs) return;
    for (const t of thread.turns) {
      if (t.status === 'working' && t.jobId && askJobs[t.jobId]) runner.settleJob(t.id, askJobs[t.jobId]);
    }
  }, [askJobs, thread]);

  const pickModel = useCallback((id) => {
    setModel(id);
    try { localStorage.setItem(MODEL_KEY, id); } catch { /* private mode — the pick just won't persist */ }
  }, []);

  // Rung 0 needs the rule DEFINITIONS beside the results — prepare returns slim
  // results only, so pair them with the suite already loaded for the Rules list.
  const preparedForAsk = useMemo(() => (
    prepared && prepared.ok && suiteChecks && suiteChecks.length
      ? { checks: suiteChecks, results: prepared.results }
      : null
  ), [prepared, suiteChecks]);

  // The rows POSTED with a question are the only ones the client can name in an evidence
  // jump (see resolveRowRef), so their requestIds are snapshotted onto the turn at ask
  // time. A whole-session question posts nothing and snapshots nothing.
  const submit = useCallback((q) => {
    const runner = runnerRef.current;
    if (!runner || typeof q !== 'string') return Promise.resolve(null);
    const rowIds = Array.isArray(rows) ? rows.map((r) => (r && typeof r.requestId === 'string' ? r.requestId : null)) : undefined;
    return runner.ask(q, { rows, app, prepared: preparedForAsk, model }, { rowIds });
  }, [rows, app, preparedForAsk, model]);

  const handleRetry = useCallback((q) => submit(q), [submit]);
  const handleCancel = useCallback(() => { if (runnerRef.current) runnerRef.current.cancel(); }, []);
  // The evidence item (third argument) carries the server's `requestId`; it wins over the posted-rows index map.
  const handleJump = useCallback((rowRef, turn, ev) => {
    const id = resolveRowRef(rowRef, turn && turn.rowIds, ev && ev.requestId);
    if (id && typeof onJumpToRequest === 'function') onJumpToRequest(id);
  }, [onJumpToRequest]);
  const isRowLive = useCallback((rowRef, turn, ev) => (
    typeof onJumpToRequest === 'function' && resolveRowRef(rowRef, turn && turn.rowIds, ev && ev.requestId) !== null
  ), [onJumpToRequest]);

  const working = thread.turns.some((t) => t.status === 'working');
  const scopeRows = useMemo(
    () => (Array.isArray(rows) ? rows : Object.values(liveTrafficGroups || {}).flat()),
    [rows, liveTrafficGroups],
  );
  const lastScope = useMemo(() => {
    for (let i = thread.turns.length - 1; i >= 0; i -= 1) if (thread.turns[i].scope) return thread.turns[i].scope;
    return undefined;
  }, [thread]);
  const scope = useMemo(() => scopeSummary(scopeRows, { kind: 'live' }, lastScope), [scopeRows, lastScope]);

  // The model picker is the composer's `depthControl` slot. Disabled while a question runs.
  const modelControl = (
    /* No Tooltip on this control. A tooltip anchored to the FormControl
       stays up when the menu opens and floats over the first option —
       it hid "Auto" completely. The detail belongs in the options
       anyway, where the choice is being made. */
    <FormControl size="small" sx={{ minWidth: 132 }}>
      <InputLabel id="ask-model-label">Model</InputLabel>
      <Select
        labelId="ask-model-label" label="Model" value={model} disabled={working}
        onChange={(e) => pickModel(e.target.value)}
        // The closed control shows the name only — the rate and the
        // description would not fit, and the menu carries both.
        renderValue={(id) => (models.find((m) => m.id === id) || {}).label || id}
      >
        {models.map((m) => (
          <MenuItem key={m.id} value={m.id} sx={{ display: 'block', py: 0.75 }}>
            <Typography variant="body2">{modelOptionLabel(m)}</Typography>
            <Typography variant="caption" sx={{ color: 'var(--muted)' }}>
              {modelDetail(m)}
            </Typography>
          </MenuItem>
        ))}
      </Select>
    </FormControl>
  );

  return (
    <Stack spacing={1.5}>
      {/* A pick only binds the rungs that call a model. Saying so here stops the
          picker from reading as a promise that every answer costs tokens. */}
      {model !== 'auto' && (
        <Typography variant="caption" sx={{ color: 'var(--muted)' }}>
          {(models.find((m) => m.id === model) || {}).label || model} is used only when a
          question needs a model — questions the captured traffic already answers stay free.
        </Typography>
      )}

      {/* A question asked in Build lands here — say why, or the jump is silent. */}
      {redirectNotice && (
        <Alert severity="info" onClose={onDismissRedirectNotice}>{redirectNotice}</Alert>
      )}

      {/* The conversation. Examples (empty thread) and follow-up chips both submit through
          onAsk; a working turn shows the live trail and its own "Investigating" line, and
          the composer swaps Send for Cancel (which only stops waiting). A prefilled
          question from Build is put in the composer, never sent: sending spends. */}
      {working && (
        <Typography variant="caption" sx={{ color: 'var(--muted)' }}>
          The server keeps working if you leave this page or reload — the answer is kept and
          appears here when it lands.
        </Typography>
      )}
      <AskThread
        thread={thread} scope={scope}
        onSend={submit} onAsk={submit} onCancel={handleCancel} onRetry={handleRetry}
        onJump={handleJump} isRowLive={isRowLive}
        depthControl={modelControl} renderExtra={(t) => <AskExtras turn={t} />}
        composerInitialText={initialQuestion}
      />
    </Stack>
  );
}

/* ─── Rules ───────────────────────────────────────────────────────────────── */

/* ─── Rule categories ────────────────────────────────────────────────────────
 * Display order, roughly the order a request lives through: what we sent, how
 * it was shaped, what came back, what happened to it, and the cross-cutting
 * concerns last. Categories are authored on the checks themselves
 * (conformance/suites/*.checks.json); anything not listed here still renders,
 * in a group at the end, so adding a category server-side is never a crash.
 */
const CATEGORY_ORDER = [
  'GAM request',
  'GAM video / IMA request',
  'Prebid request',
  'Amazon APS request',
  'SpringServe request',
  'FreeWheel request',
  'OpenRTB schema',
  'VAST document',
  'Bids & pricing',
  'Delivery & measurement',
  'Privacy & consent',
  'Supply chain',
  'Pacing & timing',
  'Transport & hygiene',
];
const UNCATEGORIZED = 'Other rules';

// Rank of a status when deciding whether a section opens itself. A section
// holding a failure is the one you came to look at.
const ALARM = { fail: 3, error: 3, warn: 2, warning: 2 };

/**
 * The one place a check's category is normalized. The status strip filters by
 * it and the Rules list groups by it — two spellings of this rule would mean a
 * pill that selects a category the list then renders as empty.
 */
function categoryOf(check) {
  const raw = check && check.category;
  return (typeof raw === 'string' && raw.trim()) ? raw.trim() : UNCATEGORIZED;
}

function groupByCategory(checks) {
  const byCat = new Map();
  for (const c of checks) {
    const cat = categoryOf(c);
    if (!byCat.has(cat)) byCat.set(cat, []);
    byCat.get(cat).push(c);
  }
  const known = CATEGORY_ORDER.filter((k) => byCat.has(k));
  const extra = [...byCat.keys()].filter((k) => !CATEGORY_ORDER.includes(k)).sort();
  return [...known, ...extra].map((name) => ({ name, checks: byCat.get(name) }));
}

// Per-category severity, reused by the status strip below AND the Rules list
// further down — one implementation for "what's the worst thing here" and
// "how many need attention", so the two views of the same run can never
// disagree. Ranks reuse ALARM's own fail/warn grouping (fail=error=3,
// warn=warning=2) and extend it downward (info=1, pass/ok=0), which is
// exactly the fail > warn > info > pass ordering the status strip needs.
function categoryStats(checks, resultById) {
  let bad = 0;
  let worst = null; // canonical 'fail' | 'warn' | 'info' | 'pass', or null = n/a
  let worstRank = -1;
  for (const c of checks) {
    const status = (resultById.get(c.id) || {}).status;
    if (!status) continue;
    if (ALARM[status]) bad += 1;
    const rank = ALARM[status] || (status === 'info' ? 1 : (status === 'pass' || status === 'ok' ? 0 : -1));
    if (rank > worstRank) {
      worstRank = rank;
      worst = status === 'error' ? 'fail' : status === 'warning' ? 'warn' : status === 'ok' ? 'pass' : status;
    }
  }
  return { bad, worst };
}

/* ─── Status strip ───────────────────────────────────────────────────────────
 * The at-a-glance "is each vendor's pipeline working?" read the old standalone
 * Smoke Test page existed for. Not a new computation — groupByCategory and
 * categoryStats are the exact grouping/counting the Rules list below already
 * does; this just surfaces the worst status per category above the fold
 * instead of inside a collapsed accordion.
 *
 * A pill is a FILTER SELECTION: clicking one narrows the Rules list to that
 * category and lists every rule in it, clicking it again clears back to all.
 * It used to merely force the category's accordion open, which left the other
 * thirteen sections in the list — so "show me the GAM rules" still meant
 * scrolling past everything else. Selection state lives in AgentPanel beside
 * catOverride so the strip and the list can never disagree about what is
 * being shown.
 */
function StatusStrip({ suiteChecks, prepared, selectedCat, onSelectCategory }) {
  const resultById = useMemo(() => {
    const m = new Map();
    for (const r of (prepared && prepared.results) || []) m.set(r.checkId, r);
    return m;
  }, [prepared]);

  const groups = useMemo(() => groupByCategory(suiteChecks), [suiteChecks]);

  if (!groups.length) return null;

  return (
    <Stack direction="row" spacing={0.75} flexWrap="wrap" useFlexGap>
      {groups.map((g) => {
        const { bad, worst } = categoryStats(g.checks, resultById);
        const isSel = selectedCat === g.name;
        return (
          <Chip
            key={g.name}
            size="small"
            clickable
            onClick={() => onSelectCategory(g.name)}
            color={worst ? statusColor(worst) : 'default'}
            variant={worst ? 'filled' : 'outlined'}
            aria-pressed={isSel}
            label={worst ? `${g.name} · ${worst}${bad ? ` (${bad})` : ''}` : `${g.name} · n/a`}
            // The selected pill gets a ring and the rest fade back, so the
            // strip reads as "one of these is doing the filtering" rather than
            // as fourteen equal buttons. Severity colour still carries the
            // status — the ring is added ON TOP of it, never instead of it.
            sx={{
              fontWeight: isSel ? 700 : undefined,
              outline: isSel ? '2px solid currentColor' : 'none',
              outlineOffset: 2,
              opacity: (selectedCat && !isSel) ? 0.45 : 1,
              transition: 'opacity .15s',
            }}
          />
        );
      })}
    </Stack>
  );
}

function RuleDetail({ result }) {
  // Only the verdict side. What the rule LOOKS FOR — the source, the operator,
  // the scope — moved to the Rules tab on Checks, which is the view for editing rules
  // rather than reading a session. Keeping it here made the same question
  // answerable in two places and neither one complete.
  const failing = Number(result && result.failureCount) || 0;
  const rows = result ? [
    { label: 'Matched', value: `${result.matched ?? 0} request(s)${failing ? ` · ${failing} failing` : ''}` },
    ...(result.note ? [{ label: 'Why n/a', value: result.note, mono: false }] : []),
  ] : [{ label: 'Matched', value: 'not run yet — press Compile', mono: false }];

  return (
    <Box sx={{ px: 2, py: 1.25, bgcolor: 'action.hover' }}>
      {rows.map((r) => (
        <Stack key={r.label} direction="row" spacing={2} sx={{ py: 0.35 }}>
          <Typography variant="body2" sx={{ minWidth: 108, color: 'var(--muted)', fontSize: 12 }}>
            {r.label}
          </Typography>
          <Typography
            variant="body2"
            sx={{ fontSize: 12, fontFamily: r.mono === false ? undefined : 'ui-monospace, Menlo, monospace', wordBreak: 'break-word' }}
          >
            {r.value}
          </Typography>
        </Stack>
      ))}
      <ValuesRead reads={result && result.reads} matched={result && result.matched} />
    </Box>
  );
}

/* The answer to "what did those N requests actually carry".
 *
 * Grouped by the VALUE that was read rather than listed per request: a rule
 * over 300 consistent requests collapses to one line, and the moment two
 * values appear where one was expected, that IS the finding — it does not have
 * to be spotted by scrolling 300 identical rows.
 *
 * The counts come from evaluateCheck's row projection, which is capped at 50.
 * Above that they describe a SAMPLE, and this says so rather than letting the
 * number read as a total — see summarizeReads in conformance/agentPrepare.js.
 */
function ValuesRead({ reads, matched }) {
  if (!reads || !Array.isArray(reads.groups) || !reads.groups.length) return null;
  const { groups, distinct, truncated, sampled, sampleTruncated } = reads;
  const scope = sampleTruncated
    ? `first ${sampled} of ${matched} matched`
    : `${sampled} request(s)`;
  return (
    <Box sx={{ mt: 1 }}>
      <Stack direction="row" spacing={1} alignItems="baseline" sx={{ mb: 0.5 }}>
        <Typography variant="caption" sx={{ letterSpacing: '.08em', color: 'var(--muted)' }}>
          VALUES READ
        </Typography>
        <Typography variant="caption" sx={{ color: 'var(--muted)' }}>
          {distinct} distinct · {scope}
        </Typography>
        {distinct > 1 && <Chip size="small" color="warning" variant="outlined" label="not uniform" />}
      </Stack>
      {groups.map((g) => (
        <Stack
          key={`${g.ok ? 'ok' : 'no'}:${g.value}`}
          direction="row" spacing={2} alignItems="baseline"
          sx={{ py: 0.4, borderTop: '1px solid', borderColor: 'divider' }}
        >
          <Typography variant="body2" sx={{ width: 14, color: g.ok ? 'success.main' : 'error.main', fontSize: 12 }}>
            {g.ok ? '✓' : '✗'}
          </Typography>
          <Typography
            variant="body2"
            sx={{ flex: 1, fontSize: 12, fontFamily: 'ui-monospace, Menlo, monospace', wordBreak: 'break-all' }}
          >
            {g.value}
          </Typography>
          <Typography variant="body2" sx={{ fontSize: 12, color: 'var(--muted)', whiteSpace: 'nowrap' }}>
            {g.count}×
          </Typography>
        </Stack>
      ))}
      {truncated && (
        <Typography variant="caption" sx={{ display: 'block', mt: 0.5, color: 'var(--muted)' }}>
          + {distinct - groups.length} more distinct value(s) not shown
        </Typography>
      )}
    </Box>
  );
}

function RulesList({ suiteChecks, loading, error, prepared, catOverride, setCatOverride, selectedCat, onClearCategory }) {
  const [quarantined, setQuarantined] = useState(new Set());
  const [note, setNote] = useState(null);
  const [filter, setFilter] = useState('');
  // name -> explicit open/closed, set only by clicking (here, or via the
  // status strip above). Absent means "follow whatever the section would do
  // on its own" (see autoOpen), so a reader's click always wins over the
  // auto-open and can close a section it opened. Lifted to AgentPanel — see
  // catOverride's declaration there — so the status strip's pills can force a
  // category open through the same state this list's own toggleCat writes to.
  // At most one rule open at a time — this is a "look at this one" affordance,
  // not a second list to scroll.
  const [openRule, setOpenRule] = useState(null);

  const refreshQuarantine = useCallback(() => {
    listQuarantine()
      .then((r) => setQuarantined(new Set((r?.quarantined || []).map((q) => q.checkId))))
      .catch(() => { /* a failed read just shows nothing disabled */ });
  }, []);
  useEffect(refreshQuarantine, [refreshQuarantine]);

  const resultById = useMemo(() => {
    const m = new Map();
    for (const r of (prepared && prepared.results) || []) m.set(r.checkId, r);
    return m;
  }, [prepared]);

  // Declared BEFORE `visible`: its dependency array references `names`, and a
  // deps array is evaluated during render — a later `const` puts it in the
  // temporal dead zone and crashes the whole page, not just this panel.
  const names = useMemo(() => nameRules(suiteChecks), [suiteChecks]);

  // Filter across everything a person might type: the rule name, the rule
  // itself, the engineering description, and the traffic it applies to. A
  // filter that only matched one field would look broken on the other three.
  // The status-strip selection narrows the list; the text box then searches
  // WITHIN that narrowing. Order matters only for the counts we report (the
  // placeholder says how many rules the box is searching over), but composing
  // them is what makes "GAM request" + "sz" a sensible thing to type.
  const scoped = useMemo(() => (
    selectedCat ? suiteChecks.filter((c) => categoryOf(c) === selectedCat) : suiteChecks
  ), [suiteChecks, selectedCat]);

  const visible = useMemo(() => {
    const q = filter.trim().toLowerCase();
    if (!q) return scoped;
    return scoped.filter((c) => {
      const types = c.target && c.target.requestType;
      // The derived name (the parameter key) has to be searchable — typing
      // `sz` must find the sz rules, and the id spells some keys differently.
      const hay = [
        names.get(c.id), c.id, c.statement, c.description, c.category,
        ruleExpression(c), Array.isArray(types) ? types.join(' ') : types,
      ].filter(Boolean).join(' ').toLowerCase();
      return hay.includes(q);
    });
  }, [scoped, filter, names]);

  const groups = useMemo(() => groupByCategory(visible), [visible]);

  // Sections that opened themselves, as opposed to sections the reader opened.
  // A filter is a search, so every surviving section opens; and after a run the
  // sections holding a warn/fail open, because those are what the run was for.
  const autoOpen = useMemo(() => {
    // A selection means the reader asked for exactly these rules, so the one
    // surviving section opens — a filter that lands on a collapsed accordion
    // looks like it returned nothing.
    if (selectedCat || filter.trim()) return new Set(groups.map((g) => g.name));
    const hot = new Set();
    for (const g of groups) {
      for (const c of g.checks) {
        const r = resultById.get(c.id);
        if (r && ALARM[r.status]) { hot.add(g.name); break; }
      }
    }
    return hot;
  }, [groups, filter, selectedCat, resultById]);

  const isOpen = useCallback(
    (name) => (catOverride.has(name) ? catOverride.get(name) : autoOpen.has(name)),
    [catOverride, autoOpen],
  );

  const toggleCat = useCallback((name) => {
    setCatOverride((prev) => {
      const next = new Map(prev);
      next.set(name, !(prev.has(name) ? prev.get(name) : autoOpen.has(name)));
      return next;
    });
  }, [autoOpen, setCatOverride]);

  // The baseline is a COMMITTED builtin — suiteStore rejects writes to it by
  // name ("builtin suite (read-only) — choose another name"). So "disable" is
  // quarantine (the engine softens the verdict from the next run), not a delete,
  // and rules you write go to a user suite that runs alongside it.
  if (loading) return <CircularProgress size={20} />;
  if (error) return <Alert severity="error">{error}</Alert>;

  return (
    <Stack spacing={1.5}>
      {/* Read-only on purpose. Writing and disabling rules lives on the Rules
          tab — that is a rare, deliberate job, and putting its
          controls on every row made a list people were only trying to READ
          twice as busy. */}
      <Typography variant="caption" sx={{ color: 'var(--muted)' }}>
        The adtech validation rules this session ran, and what each one found. To write a
        rule or switch one off, use the <b>Rules</b> tab above.
      </Typography>

      {note && <Alert severity="info" onClose={() => setNote(null)}>{note}</Alert>}

      {/* The narrowing has to be visible and reversible from HERE too. The pill
          that set it is above the fold and may be scrolled off, and a list
          silently showing 11 of 168 rules with no stated reason is the bug
          this whole affordance exists to avoid. */}
      {selectedCat && (
        <Stack direction="row" spacing={1} alignItems="center" flexWrap="wrap" useFlexGap>
          <Typography variant="caption" sx={{ color: 'var(--muted)' }}>Showing</Typography>
          <Chip size="small" label={selectedCat} onDelete={onClearCategory} />
          <Typography variant="caption" sx={{ color: 'var(--muted)' }}>
            {`${scoped.length} of ${suiteChecks.length} rules — clear to see all`}
          </Typography>
        </Stack>
      )}

      <TextField
        size="small" fullWidth value={filter} onChange={(e) => setFilter(e.target.value)}
        placeholder={selectedCat
          ? `Filter ${scoped.length} ${selectedCat} rules — name, wording, or traffic type`
          : `Filter ${suiteChecks.length} rules — name, wording, or traffic type`}
      />

      <TableContainer component={Paper} variant="outlined" sx={{ maxHeight: 480 }}>
        <Table size="small" stickyHeader sx={{ tableLayout: 'fixed' }}>
          <TableHead>
            <TableRow>
              <TableCell sx={{ width: '24%' }}>Rule</TableCell>
              <TableCell sx={{ width: '40%' }}>Requires</TableCell>
              <TableCell sx={{ width: '13%' }}>Applies to</TableCell>
              <TableCell sx={{ width: '23%' }} align="right">Result</TableCell>
            </TableRow>
          </TableHead>
          <TableBody>
            {!groups.length && (
              <TableRow>
                <TableCell colSpan={4} sx={{ color: 'var(--muted)', fontSize: 12 }}>
                  No rule matches “{filter}”{selectedCat ? ` in ${selectedCat}` : ''}.
                </TableCell>
              </TableRow>
            )}
            {groups.map((g) => {
              const open = isOpen(g.name);
              // Counted per section so a collapsed section still reports what it
              // is hiding — a rolled-up failure must never be invisible.
              const { bad } = categoryStats(g.checks, resultById);
              const off = g.checks.filter((c) => quarantined.has(c.id)).length;
              return (
                <React.Fragment key={g.name}>
                  <TableRow
                    hover onClick={() => toggleCat(g.name)}
                    sx={{ cursor: 'pointer', bgcolor: 'action.hover' }}
                  >
                    <TableCell colSpan={4} sx={{ py: 0.75 }}>
                      <Stack direction="row" spacing={1} alignItems="center">
                        <ExpandMoreIcon
                          fontSize="small"
                          sx={{ transform: open ? 'none' : 'rotate(-90deg)', transition: 'transform .15s', color: 'var(--muted)' }}
                        />
                        <Typography variant="body2" sx={{ fontWeight: 600 }}>{g.name}</Typography>
                        <Typography variant="caption" sx={{ color: 'var(--muted)' }}>{g.checks.length}</Typography>
                        {bad > 0 && <Chip size="small" color="warning" label={`${bad} to look at`} />}
                        {off > 0 && <Chip size="small" variant="outlined" label={`${off} disabled`} />}
                      </Stack>
                    </TableCell>
                  </TableRow>
                  {open && g.checks.map((c) => {
              const r = resultById.get(c.id);
              const types = c.target && c.target.requestType;
              const isOff = quarantined.has(c.id);
              return (
                <React.Fragment key={c.id}>
                <TableRow
                  hover
                  onClick={() => setOpenRule((prev) => (prev === c.id ? null : c.id))}
                  sx={{ cursor: 'pointer', ...(isOff ? { opacity: 0.5 } : {}) }}
                >
                  <TableCell
                    title={c.id}
                    sx={{ fontFamily: 'ui-monospace, Menlo, monospace', fontSize: 12, overflowWrap: 'anywhere' }}
                  >
                    {names.get(c.id) || c.id}
                  </TableCell>
                  {/* `statement` is the rule in ad-tech vocabulary; `description` is
                      the compiler-facing text (param paths, validator names, spec
                      sections) and belongs on hover, not in the table. */}
                  <TableCell sx={{ fontSize: 12, wordBreak: 'break-word' }} title={c.description || undefined}>
                    {c.statement || c.description || '—'}
                  </TableCell>
                  {/* overflowWrap, not wordBreak: break-word split request types
                      mid-token ("freewheel_trackin g"), which reads as a typo. */}
                  <TableCell sx={{ fontSize: 11, overflowWrap: 'anywhere', hyphens: 'none' }}>
                    {Array.isArray(types) ? types.join(', ') : (types || 'any')}
                  </TableCell>
                  <TableCell align="right">
                    {r ? <Chip size="small" color={statusColor(r.status)} label={r.status} />
                      : <Typography variant="caption" sx={{ color: 'var(--muted)' }}>—</Typography>}
                  </TableCell>
                </TableRow>
                {openRule === c.id && (
                  <TableRow>
                    <TableCell colSpan={4} sx={{ p: 0, borderBottom: '1px solid', borderColor: 'divider' }}>
                      <RuleDetail result={r} />
                    </TableCell>
                  </TableRow>
                )}
                </React.Fragment>
              );
                  })}
                </React.Fragment>
              );
            })}
          </TableBody>
        </Table>
      </TableContainer>
    </Stack>
  );
}

/* ─── the panel ───────────────────────────────────────────────────────────── */

export default function AgentPanel({
  rows, app, driver, initialQuestion, redirectNotice, onDismissRedirectNotice, onJumpToRequest,
}) {
  const [suite, setSuite] = useState(BASELINE);
  const [prepared, setPrepared] = useState(null);
  const [suites, setSuites] = useState([{ name: BASELINE }]);
  const [suiteChecks, setSuiteChecks] = useState([]);
  const [rulesLoading, setRulesLoading] = useState(false);
  const [rulesError, setRulesError] = useState(null);
  // name -> explicit open/closed for a Rules-list category. Lives here, not
  // inside RulesList, so the status strip's pills and RulesList's own
  // toggleCat share one state instead of two that could disagree.
  const [catOverride, setCatOverride] = useState(() => new Map());
  // Which status-strip pill is acting as the Rules-list filter, or null for
  // "all rules". Separate from catOverride: that one is per-section open/closed
  // bookkeeping, this one decides which rules are in the list at all.
  const [selectedCat, setSelectedCat] = useState(null);
  // Controls the Rules accordion itself — a pill click has to open it even
  // when nobody has touched the accordion header yet.
  const [rulesExpanded, setRulesExpanded] = useState(false);

  useEffect(() => {
    let cancelled = false;
    listSuites()
      .then((out) => {
        if (cancelled) return;
        const list = Array.isArray(out) ? out : (out && out.suites) || [];
        setSuites(list.length ? list : [{ name: BASELINE }]);
      })
      .catch(() => { /* keep the baseline-only default */ });
    return () => { cancelled = true; };
  }, []);

  // Loaded once here rather than inside the Rules list: it doubles as the rule
  // DEFINITIONS the Ask box needs to answer from an already-computed verdict.
  useEffect(() => {
    let cancelled = false;
    setRulesLoading(true); setRulesError(null);
    // A category selected against the previous suite may not exist in this
    // one, which would filter the list down to nothing with no way to tell why.
    setSelectedCat(null);
    getSuiteChecks(suite)
      .then((out) => { if (!cancelled) setSuiteChecks(Array.isArray(out) ? out : (out && out.checks) || []); })
      .catch((e) => { if (!cancelled) setRulesError(errText(e)); })
      .finally(() => { if (!cancelled) setRulesLoading(false); });
    return () => { cancelled = true; };
  }, [suite]);

  // A status-strip pill means "show me only this". It toggles: clicking the
  // pill that is already selected clears back to all rules, so the strip needs
  // no separate reset control. Selecting also forces that category's section
  // open (the list may be carrying an explicit collapse from an earlier click,
  // which would otherwise hide the very rules being selected) and expands the
  // accordion, since a selection nobody can see reads as a dead pill.
  const handleSelectCategory = useCallback((name) => {
    setSelectedCat((prev) => (prev === name ? null : name));
    setCatOverride((prev) => {
      const next = new Map(prev);
      next.set(name, true);
      return next;
    });
    setRulesExpanded(true);
  }, []);

  const handleClearCategory = useCallback(() => setSelectedCat(null), []);

  // Count for the accordion title, so a narrowed list says so while collapsed.
  const selectedCount = useMemo(() => (
    selectedCat ? suiteChecks.filter((c) => categoryOf(c) === selectedCat).length : 0
  ), [suiteChecks, selectedCat]);

  // GAM ad-tag param conformance — a drill-down beneath the Rules list,
  // reusing the exact suiteChecks/prepared.results the list above already
  // has (docs/gam-param-conformance-spec.md §3.5). Merging is done HERE,
  // not in the builder: each check's assert.param is the GAM query-param key
  // its result belongs to, and buildParamConformanceRows is a pure function
  // that only knows about already-keyed rows, not check definitions.
  //
  // buSummary is the BU/App-Pack layer: agent/prepare returns `buParams` as
  // {byKey} when a pack matched the traffic, and null when none did. Null is
  // the documented "no publisher policy configured" state (spec §3.1) and the
  // table renders it as "—", never as a finding — which is also exactly what
  // an unconfigured user sees.
  const paramConformanceRows = useMemo(() => {
    if (!prepared || !prepared.ok || !Array.isArray(suiteChecks) || !suiteChecks.length) return [];
    const resultById = new Map();
    for (const r of prepared.results || []) resultById.set(r.checkId, r);
    const merged = [];
    for (const c of suiteChecks) {
      const types = c && c.target && c.target.requestType;
      const isGoogleScoped = Array.isArray(types) ? types.includes('google') : types === 'google';
      const key = c && c.assert && typeof c.assert.param === 'string' ? c.assert.param : null;
      if (!isGoogleScoped || !key) continue;
      const r = resultById.get(c.id);
      if (!r) continue;
      merged.push({
        key,
        matched: r.matched,
        failureCount: r.failureCount,
        status: r.status,
        severity: r.severity,
        statement: c.statement,
        pattern: c.assert.pattern,
        values: c.assert.values,
      });
    }
    return buildParamConformanceRows(merged, GAM_PARAM_DICTIONARY, prepared.buParams || null);
  }, [prepared, suiteChecks]);

  // APS display field conformance — the same drill-down for `amazon` traffic.
  // Its dictionary is FETCHED, not imported: the APS knowledge pack is already
  // UI-shaped, so it is served by GET /proxy/checks/aps-params instead of being
  // forked into a frontend file that would need a drift guard (spec §6.1).
  const [apsDictionary, setApsDictionary] = useState([]);
  useEffect(() => {
    let cancelled = false;
    getApsParamDictionary()
      .then((d) => { if (!cancelled) setApsDictionary(Array.isArray(d) ? d : []); })
      .catch(() => { /* getApsParamDictionary already degrades to [] */ });
    return () => { cancelled = true; };
  }, []);

  // Which platform this session's APS traffic came from, read off the parsed
  // envelope. It decides whether an iOS-only field (pj.att, skadn, pj.api)
  // reads "n/a" or gets judged — unknown means judge, never hide.
  const apsSessionPlatform = useMemo(() => {
    for (const r of (Array.isArray(rows) ? rows : [])) {
      if (String(r?.requestType || '').toLowerCase() !== 'amazon') continue;
      const os = r?.validData?.parsed?.request?.device?.os;
      if (os) return String(os);
    }
    return null;
  }, [rows]);

  const apsParamConformanceRows = useMemo(() => {
    if (!prepared || !prepared.ok || !Array.isArray(suiteChecks) || !suiteChecks.length) return [];
    const resultById = new Map();
    for (const r of prepared.results || []) resultById.set(r.checkId, r);
    const merged = [];
    for (const c of suiteChecks) {
      const types = c && c.target && c.target.requestType;
      // 'amazon' only, never 'amazon_video': that is APS OTT/C2S, real OpenRTB
      // on entirely different paths, and mixing the two would put fields from
      // one wire format in a table describing the other.
      const isApsScoped = Array.isArray(types) ? types.includes('amazon') : types === 'amazon';
      const key = c && c.assert && typeof c.assert.param === 'string' ? c.assert.param : null;
      if (!isApsScoped || !key) continue;
      const r = resultById.get(c.id);
      if (!r) continue;
      merged.push({
        key,
        matched: r.matched,
        failureCount: r.failureCount,
        status: r.status,
        severity: r.severity,
        statement: c.statement,
        pattern: c.assert.pattern,
        values: c.assert.values,
      });
    }
    return buildApsParamConformanceRows(
      merged, apsDictionary, prepared.apsBuParams || null, apsSessionPlatform
    );
  }, [prepared, suiteChecks, apsDictionary, apsSessionPlatform]);

  // Prebid ORTB field conformance — the same drill-down for `prebid` traffic,
  // rendered by the GAM pass's ParamConformanceTable rather than a third table:
  // the columns are identical, and a copy would be a third place to fix a
  // rendering bug (docs/prebid-param-conformance-spec.md §4 WP3).
  //
  // Its dictionary is FETCHED for the same reason APS's is — the Prebid
  // knowledge pack is already UI-shaped, so there is no frontend Prebid
  // dictionary and no sync guard to keep. It arrives with an alias map that
  // collapses parsed-sourced params (`request.app.bundle`) onto the body path
  // the BU rules use (`app.bundle`); without it one field renders as two
  // half-empty rows.
  const [prebidDictionary, setPrebidDictionary] = useState({ params: {}, aliases: {} });
  useEffect(() => {
    let cancelled = false;
    getPrebidParamDictionary()
      .then((d) => { if (!cancelled) setPrebidDictionary(d || { params: {}, aliases: {} }); })
      .catch(() => { /* getPrebidParamDictionary already degrades to empty maps */ });
    return () => { cancelled = true; };
  }, []);

  const prebidParamConformanceRows = useMemo(() => {
    if (!prepared || !prepared.ok || !Array.isArray(suiteChecks) || !suiteChecks.length) return [];
    const aliases = prebidDictionary.aliases || {};
    const resultById = new Map();
    for (const r of prepared.results || []) resultById.set(r.checkId, r);
    const merged = [];
    for (const c of suiteChecks) {
      const types = c && c.target && c.target.requestType;
      const isPrebidScoped = Array.isArray(types) ? types.includes('prebid') : types === 'prebid';
      const raw = c && c.assert && typeof c.assert.param === 'string' ? c.assert.param : null;
      if (!isPrebidScoped || !raw) continue;
      // Response-side params (`response.*`, `seatbid`, `ext.responsetimemillis`)
      // describe the bid response, not the request this table is about, and
      // have no BU layer to compare against — they are left to the Rules list.
      if (raw.startsWith('response.')) continue;
      const key = aliases[raw] || raw;
      const r = resultById.get(c.id);
      if (!r) continue;
      merged.push({
        key,
        matched: r.matched,
        failureCount: r.failureCount,
        status: r.status,
        severity: r.severity,
        statement: c.statement,
        pattern: c.assert.pattern,
        values: c.assert.values,
      });
    }
    // prepared.prebidBuParams is Contract B's map under its Prebid name
    // (`byPath`, because ORTB fields are addressed by dot path). The builder
    // takes the GAM vocabulary, so it is handed the inner map directly. Null
    // when no App Pack matched — the documented "no publisher policy
    // configured" state, rendered as "—" and never as a finding.
    const bu = prepared.prebidBuParams
      ? (prepared.prebidBuParams.byPath || prepared.prebidBuParams)
      : null;
    return buildParamConformanceRows(merged, prebidDictionary.params || {}, bu);
  }, [prepared, suiteChecks, prebidDictionary]);

  return (
    <Stack spacing={2.5}>
      <CompileStrip
        rows={rows} app={app} suite={suite} setSuite={setSuite} suites={suites}
        prepared={prepared} setPrepared={setPrepared} driver={driver}
      />
      <Divider />
      <AskBox
        rows={rows} app={app} prepared={prepared} suiteChecks={suiteChecks}
        initialQuestion={initialQuestion} redirectNotice={redirectNotice}
        onDismissRedirectNotice={onDismissRedirectNotice}
        onJumpToRequest={onJumpToRequest}
      />
      {/* Per-category status strip — the "is each vendor's pipeline working?"
          read the old standalone Smoke Test page existed for, now sitting
          above the same Rules data instead of buried inside the collapsed
          accordion below. Same gate as the accordion: visible once a run
          exists. */}
      {prepared && (
        <StatusStrip
          suiteChecks={suiteChecks} prepared={prepared}
          selectedCat={selectedCat} onSelectCategory={handleSelectCategory}
        />
      )}
      {/* Only after a run. Before a run every rule reads "—" in the Result
          column, so the list is 157 rows of nothing — and the rules themselves
          are browsable any time on the Rules tab. This section exists to
          show what the RUN found. */}
      {prepared && (
        <Accordion
          variant="outlined" disableGutters sx={{ '&:before': { display: 'none' } }}
          expanded={rulesExpanded} onChange={(e, isExpanded) => setRulesExpanded(isExpanded)}
        >
          <AccordionSummary expandIcon={<ExpandMoreIcon />}>
            <Typography sx={{ fontSize: 14, fontWeight: 600 }}>
              Rules{suiteChecks.length
                ? (selectedCat ? ` (${selectedCount} of ${suiteChecks.length})` : ` (${suiteChecks.length})`)
                : ''}
            </Typography>
          </AccordionSummary>
          <AccordionDetails>
            <RulesList
              suiteChecks={suiteChecks} loading={rulesLoading}
              error={rulesError} prepared={prepared}
              catOverride={catOverride} setCatOverride={setCatOverride}
              selectedCat={selectedCat} onClearCategory={handleClearCategory}
            />
          </AccordionDetails>
        </Accordion>
      )}
      {/* The GAM param drill-down: selecting the "GAM request" status-strip
          pill already narrows the Rules list above to these rules; this is
          the per-param detail beneath it — what each param should be, what
          the session sent, and how many eligible requests fail it. */}
      {prepared && selectedCat === 'GAM request' && paramConformanceRows.length > 0 && (
        <Paper variant="outlined" sx={{ p: 2 }}>
          <Typography sx={{ fontSize: 14, fontWeight: 600, mb: 1 }}>
            GAM ad-tag param conformance
          </Typography>
          <ParamConformanceTable rows={paramConformanceRows} />
        </Paper>
      )}
      {/* The APS field drill-down, same relationship to the "Amazon APS
          request" pill as the GAM table has to "GAM request". */}
      {prepared && selectedCat === 'Amazon APS request' && apsParamConformanceRows.length > 0 && (
        <Paper variant="outlined" sx={{ p: 2 }}>
          <Typography sx={{ fontSize: 14, fontWeight: 600, mb: 1 }}>
            APS SDK field conformance
          </Typography>
          <ApsParamConformanceTable rows={apsParamConformanceRows} sessionPlatform={apsSessionPlatform} />
        </Paper>
      )}
      {/* The Prebid field drill-down. Its pill is "Prebid request", but the
          rows are built from EVERY prebid-scoped check — the suite spreads
          Prebid rules across eight categories (privacy, OpenRTB schema, bids)
          and a table showing only the 18 in one of them would read as though
          the rest were unchecked. Same choice the GAM table makes. */}
      {prepared && selectedCat === 'Prebid request' && prebidParamConformanceRows.length > 0 && (
        <Paper variant="outlined" sx={{ p: 2 }}>
          <Typography sx={{ fontSize: 14, fontWeight: 600, mb: 1 }}>
            Prebid ORTB field conformance
          </Typography>
          <ParamConformanceTable rows={prebidParamConformanceRows} />
        </Paper>
      )}
    </Stack>
  );
}
